import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, Check, MessageCircle, Mail, Loader2, ShieldCheck, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import GlowBackground from '@/components/site/GlowBackground';
import { whatsappLink, BRAND } from '@/lib/constants';
import { scoreLead, indicationFromScore, buildWhatsappSummary, SCORE_MAX } from '@/lib/scoring';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVINCES = [
  'Limpopo', 'Gauteng', 'Mpumalanga', 'North West', 'Free State',
  'KwaZulu-Natal', 'Eastern Cape', 'Western Cape', 'Northern Cape',
];
const YES_NO_UNSURE = ['Yes', 'No', 'Unsure'];
const YES_NO = ['Yes', 'No'];
const NIGHTS = ['0', '1', '2-3', '4-7', '8+'];
const AGENCIES = ['SAPS', 'Metro Police', 'Traffic', 'Private security', 'Other'];
const OUTCOMES = ['Withdrawn', 'Dismissed', 'Acquitted', 'Convicted', 'Still pending', 'Never charged'];

// 14 focused questions — sequenced to establish location, then Sekhoto jurisdictional facts,
// then s 35 Constitution compliance, then damages triggers, then contact.
const QUESTIONS = [
  { key: 'province',        kind: 'select', label: 'Which province did the arrest happen in?', options: PROVINCES, help: 'We use this to refer to the right attorney.' },
  { key: 'city',            kind: 'text-short', label: 'Which city or town?', placeholder: 'e.g. Polokwane, Soweto, Cape Town' },
  { key: 'arrestDate',      kind: 'date', label: 'Approximate date of arrest', help: 'A rough date is fine.' },
  { key: 'arrestingAgency', kind: 'chips', label: 'Who arrested you?', options: AGENCIES, help: 'SAPS matters are typically brought against the Minister of Police.' },
  { key: 'hadWarrant',      kind: 'chips', label: 'Did the officers show or produce a warrant?', options: YES_NO_UNSURE, help: 'A warrantless arrest must meet the strict tests in section 40 of the CPA.' },
  { key: 'reasonExplained', kind: 'chips', label: 'Were you told the reason for the arrest?', options: YES_NO_UNSURE, help: 'Section 35(1)(a) of the Constitution requires this.' },
  { key: 'noticeOfRights',  kind: 'chips', label: 'Were you given a Notice of Rights (SAP 14A) at the time of arrest?', options: YES_NO_UNSURE, help: 'SAPS is required to hand this to every arrested person. Do you still have it, or a copy?' },
  { key: 'allegedOffence',  kind: 'text-short', label: 'What offence were you accused of?', placeholder: 'e.g. theft, assault, drunk driving — leave blank if unsure', optional: true },
  { key: 'nightsDetained',  kind: 'chips', label: 'How many nights were you detained?', options: NIGHTS },
  { key: 'broughtToCourt48h', kind: 'chips', label: 'Were you brought before a court within 48 hours?', options: YES_NO_UNSURE, help: 'Section 35(1)(d) — a strict constitutional deadline.' },
  { key: 'chargesOutcome',  kind: 'chips', label: 'What happened with the charges?', options: OUTCOMES },
  { key: 'wasAssaulted',    kind: 'chips', label: 'Were you assaulted or injured during the arrest or detention?', options: YES_NO },
  { key: 'description',     kind: 'text', label: 'Anything else we should know?', help: 'Optional — a short description helps our review.', optional: true },
  { key: 'contact',         kind: 'contact', label: 'Where can we reach you?' },
];

function ProgressBar({ current, total }) {
  const pct = (current / total) * 100;
  return (
    <div className="w-full h-1 bg-white/5 overflow-hidden rounded-full" data-testid="calculator-progress">
      <motion.div className="h-full bg-[#0A84FF]" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} />
    </div>
  );
}

function SignalChip({ score }) {
  const ind = indicationFromScore(score);
  const pct = Math.round((score / SCORE_MAX) * 100);
  const dot = score >= 12 ? 'bg-[#FFD400]' : score >= 8 ? 'bg-[#39A8FF]' : score >= 4 ? 'bg-white/80' : 'bg-white/40';
  return (
    <motion.div
      data-testid="calc-signal-chip"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-[#121212]/70 backdrop-blur px-4 py-2"
    >
      <Activity className="h-3.5 w-3.5 text-[#39A8FF]" />
      <span className="flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-full ${dot} animate-pulse`} />
        <span className="text-[10px] font-inter font-bold uppercase tracking-[0.22em] text-white/85">{ind.label}</span>
      </span>
      <span className="text-[10px] font-inter font-bold text-[#FFD400] tabular-nums tracking-wider">{score}/{SCORE_MAX} · {pct}%</span>
    </motion.div>
  );
}

function ChipGroup({ options, value, onChange, testKey }) {
  return (
    <div className="flex flex-wrap gap-2.5" data-testid={`calc-chips-${testKey}`}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`calc-chip-${testKey}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => onChange(opt)}
            className={`min-h-[52px] rounded-full px-6 text-sm font-poppins font-semibold uppercase tracking-wider transition-all ${
              active
                ? 'bg-[#FFD400] text-[#07111F] border border-[#FFD400] shadow-[0_0_30px_rgba(255,212,0,0.25)]'
                : 'bg-[#121212] border border-white/10 text-white/85 hover:border-[#39A8FF]'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const initialAnswers = {
  province: '', city: '', arrestDate: '',
  arrestingAgency: '', hadWarrant: '', reasonExplained: '',
  noticeOfRights: '',
  allegedOffence: '', nightsDetained: '', broughtToCourt48h: '',
  chargesOutcome: '', wasAssaulted: '',
  description: '',
  name: '', phone: '', email: '',
};

export default function Calculator() {
  const [phase, setPhase] = useState('intro');
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [answers, setAnswers] = useState(initialAnswers);

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];

  const liveScore = useMemo(() => scoreLead(answers), [answers]);
  const showSignal = liveScore > 0 && phase === 'questions';

  const currentValid = useMemo(() => {
    if (q.optional) return true;
    if (q.kind === 'contact') return answers.name.trim().length > 1 && answers.phone.trim().length >= 6;
    if (q.kind === 'text' || q.kind === 'text-short') return answers[q.key].trim().length >= 2;
    return !!answers[q.key];
  }, [q, answers]);

  const setField = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const next = () => {
    if (!currentValid) return;
    if (step < total - 1) setStep(step + 1);
    else submit();
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  async function submit() {
    setPhase('submitting');
    const score = scoreLead(answers);
    const indication = indicationFromScore(score);
    const summary = buildWhatsappSummary({ ...answers, _score: score }, indication);
    const url = whatsappLink(summary);
    let serverAutoSent = false;
    try {
      const payload = {
        ...answers,
        source: 'calculator',
        website: '', // honeypot
        description: (answers.description ? answers.description + '\n\n' : '') +
          `[Auto] Indication: ${indication.label} (score ${score}/${SCORE_MAX}) — ${indication.description}` +
          `\n[Location] ${answers.city ? answers.city + ', ' : ''}${answers.province}` +
          `\n[Agency] ${answers.arrestingAgency}` +
          `\n[Alleged offence] ${answers.allegedOffence || '—'}` +
          `\n[Warrant] ${answers.hadWarrant} | [Reason explained] ${answers.reasonExplained} | [Notice of Rights] ${answers.noticeOfRights}` +
          `\n[Nights] ${answers.nightsDetained} | [48h court] ${answers.broughtToCourt48h}` +
          `\n[Outcome] ${answers.chargesOutcome} | [Assault] ${answers.wasAssaulted}`,
      };
      const resp = await axios.post(`${API}/leads`, payload);
      // Backend reports whether server-side WhatsApp+email were dispatched via /api/health
      // We don't need to check here — if it's configured, message is already on its way.
      serverAutoSent = !!(resp?.data && resp?.data?.id);
    } catch (err) {
      console.error(err);
      toast.error('Saved locally — you can also chat on WhatsApp below.');
    }
    setResult({ score, indication, whatsappUrl: url, summary, serverAutoSent });
    setPhase('success');
  }

  return (
    <div className="relative min-h-screen pt-24 md:pt-28 pb-40 overflow-hidden" data-testid="calculator-page">
      <GlowBackground />
      <div className="relative mx-auto max-w-3xl px-5 md:px-8">
        <AnimatePresence mode="wait">
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 md:mt-12"
              data-testid="calculator-intro"
            >
              <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Free · 3 minutes · Confidential</div>
              <h1 className="mt-5 font-poppins font-black tracking-[-0.02em] text-white text-5xl md:text-7xl leading-[0.95]">
                Free <span className="text-[#FFD400]">claim assessment.</span>
              </h1>
              <p className="mt-6 max-w-xl font-inter text-white/75 text-base md:text-lg leading-relaxed">
                Answer 13 short questions grounded in South African law. If your matter has grounds to investigate, a South African attorney will be in touch — usually via WhatsApp.
              </p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                {[
                  'Takes under 3 minutes',
                  'No cost, no obligation',
                  'Reviewed by SA attorneys',
                  'POPIA-compliant handling',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 font-inter text-white/80 text-sm">
                    <Check className="h-4 w-4 mt-0.5 text-[#39A8FF]" /> {t}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPhase('questions')}
                data-testid="calculator-start-btn"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#FFD400] px-8 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F] hover:shadow-[0_0_60px_rgba(255,212,0,0.4)] transition-shadow"
              >
                Start My Assessment <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}

          {phase === 'questions' && (
            <motion.div key="questions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-6 md:mt-12">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">
                  Question {step + 1} <span className="text-white/40">/ {total}</span>
                </div>
                <div className="flex items-center gap-3">
                  {showSignal && <SignalChip score={liveScore} />}
                  <Link to="/" data-testid="calculator-exit" className="text-[11px] font-inter font-semibold uppercase tracking-widest text-white/50 hover:text-white">Exit</Link>
                </div>
              </div>
              <ProgressBar current={step + 1} total={total} />

              <div className="mt-10 md:mt-14 min-h-[380px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <h2 data-testid={`calc-question-${step}`} className="font-poppins font-bold text-white text-3xl md:text-4xl leading-tight tracking-tight">
                      {q.label}
                    </h2>
                    {q.help && <p className="mt-3 font-inter text-white/55 text-sm">{q.help}</p>}

                    <div className="mt-8">
                      {q.kind === 'select' && (
                        <select
                          data-testid={`calc-input-${q.key}`}
                          value={answers[q.key]}
                          onChange={(e) => setField(q.key, e.target.value)}
                          className="w-full min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none"
                        >
                          <option value="">Select {q.key === 'province' ? 'a province' : '—'}</option>
                          {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      )}
                      {q.kind === 'date' && (
                        <input
                          type="date"
                          data-testid={`calc-input-${q.key}`}
                          value={answers[q.key]}
                          onChange={(e) => setField(q.key, e.target.value)}
                          className="w-full min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none"
                        />
                      )}
                      {q.kind === 'chips' && (
                        <ChipGroup
                          testKey={q.key}
                          options={q.options}
                          value={answers[q.key]}
                          onChange={(v) => setField(q.key, v)}
                        />
                      )}
                      {q.kind === 'text-short' && (
                        <input
                          type="text"
                          data-testid={`calc-input-${q.key}`}
                          value={answers[q.key]}
                          onChange={(e) => setField(q.key, e.target.value)}
                          placeholder={q.placeholder || ''}
                          className="w-full min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none"
                          maxLength={80}
                        />
                      )}
                      {q.kind === 'text' && (
                        <textarea
                          data-testid={`calc-input-${q.key}`}
                          rows={5}
                          value={answers[q.key]}
                          onChange={(e) => setField(q.key, e.target.value)}
                          placeholder="What happened, where, and any details you'd like us to know…"
                          className="w-full bg-[#121212] border border-white/10 rounded-xl p-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none resize-none"
                          maxLength={2000}
                        />
                      )}
                      {q.kind === 'contact' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input type="text" required data-testid="calc-input-name"
                            value={answers.name} onChange={(e) => setField('name', e.target.value)}
                            placeholder="Full name"
                            className="md:col-span-2 min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none"
                            maxLength={120} />
                          <input type="tel" required data-testid="calc-input-phone"
                            value={answers.phone} onChange={(e) => setField('phone', e.target.value)}
                            placeholder="Phone number (WhatsApp)"
                            className="min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none"
                            maxLength={40} />
                          <input type="email" data-testid="calc-input-email"
                            value={answers.email} onChange={(e) => setField('email', e.target.value)}
                            placeholder="Email (optional)"
                            className="min-h-[56px] bg-[#121212] border border-white/10 rounded-xl px-4 text-white font-inter text-base focus:border-[#39A8FF] focus:outline-none"
                            maxLength={180} />
                          <p className="md:col-span-2 text-xs font-inter text-white/50 leading-relaxed">
                            By continuing you consent to being contacted about your assessment. Your details are handled confidentially in line with POPIA. See our <Link to="/privacy" className="underline">Privacy Policy</Link>.
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-10 flex items-center justify-between gap-3">
                <button
                  onClick={prev}
                  disabled={step === 0}
                  data-testid="calc-prev-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-3 text-xs font-poppins font-semibold uppercase tracking-wider text-white/80 disabled:opacity-30"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={next}
                  disabled={!currentValid}
                  data-testid="calc-next-btn"
                  className={`inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-poppins font-bold uppercase tracking-wide transition-all ${
                    currentValid
                      ? 'bg-[#FFD400] text-[#07111F] hover:shadow-[0_0_40px_rgba(255,212,0,0.4)]'
                      : 'bg-white/10 text-white/40 cursor-not-allowed'
                  }`}
                >
                  {step === total - 1 ? 'Submit Assessment' : 'Continue'} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {phase === 'submitting' && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-20 md:mt-32 flex flex-col items-center text-center" data-testid="calc-submitting">
              <Loader2 className="h-10 w-10 text-[#39A8FF] animate-spin" />
              <div className="mt-6 font-poppins font-semibold text-white text-2xl">Reviewing your responses…</div>
              <div className="mt-2 font-inter text-white/60 text-sm max-w-md">Please stay on this page while we save your assessment.</div>
            </motion.div>
          )}

          {phase === 'success' && (
            <motion.div key="success" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mt-8 md:mt-14" data-testid="calc-success">
              <div className="flex items-center gap-3 text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FFD400]" /> Assessment received
              </div>
              <h1 className="mt-5 font-poppins font-black tracking-[-0.02em] text-white text-4xl md:text-6xl leading-[0.95]">
                {result?.indication?.label || 'Assessment complete'}.
              </h1>
              <p className="mt-6 max-w-2xl font-inter text-white/80 text-lg leading-relaxed">
                {result?.indication?.description || 'A South African attorney will review your responses.'}
              </p>

              {result && (
                <div className="mt-8 max-w-lg" data-testid="calc-score">
                  <div className="flex items-center justify-between text-xs font-inter uppercase tracking-widest text-white/50">
                    <span>Signal strength</span>
                    <span className="text-white">{result.score}/{SCORE_MAX}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, (result.score / SCORE_MAX) * 100)}%` }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full bg-gradient-to-r from-[#39A8FF] to-[#FFD400]"
                    />
                  </div>
                </div>
              )}

              <div className="mt-8 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/[0.06] p-5 flex items-start gap-3">
                <MessageCircle className="h-5 w-5 mt-0.5 text-[#25D366] flex-shrink-0" />
                <p className="font-inter text-white/85 text-sm leading-relaxed">
                  <span className="text-white font-semibold">Your assessment has been sent.</span> A South African attorney has received your responses and the calculated indication and will be in touch — usually via WhatsApp within business hours. You do not need to do anything else.
                </p>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href={result?.whatsappUrl || whatsappLink()} target="_blank" rel="noreferrer noopener" data-testid="calc-success-whatsapp"
                   className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD400] px-7 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F]">
                  <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
                <a href={`mailto:${BRAND.email}`} data-testid="calc-success-email"
                   className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-inter font-semibold text-white hover:border-[#39A8FF]">
                  <Mail className="h-4 w-4 text-[#39A8FF]" /> Email Us Instead
                </a>
              </div>

              <div className="mt-12 rounded-2xl border border-white/10 bg-[#121212]/60 p-6 flex items-start gap-4">
                <ShieldCheck className="h-6 w-6 text-[#39A8FF] mt-0.5" />
                <p className="font-inter text-white/60 text-sm leading-relaxed">
                  This assessment is not legal advice. Each matter is assessed on its own facts and applicable law. A successful outcome is not guaranteed.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
